/**
 * Theme Context Provider
 * Manages theme state and integrates with existing CSS custom properties system
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === null) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('access_token') || localStorage.getItem('token');
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!getAuthToken();
  };

  // Apply theme to document
  const applyTheme = useCallback((themeName) => {
    if (themeName === 'dark') {
      document.body.classList.add('dark-mode');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.body.classList.remove('dark-mode');
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, []);

  // Load user's theme preference from server
  const loadUserThemePreference = useCallback(async () => {
    if (!isAuthenticated()) {
      // For unauthenticated users, check localStorage or default to light
      const localTheme = localStorage.getItem('theme') || 'light';
      setTheme(localTheme);
      applyTheme(localTheme);
      setIsLoading(false);
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch('https://deepmlbe.maloyan.xyz/api/user/theme', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTheme(data.theme);
        applyTheme(data.theme);
        setError(null);
      } else if (response.status === 401) {
        // Token expired or invalid, fall back to local storage
        const localTheme = localStorage.getItem('theme') || 'light';
        setTheme(localTheme);
        applyTheme(localTheme);
      } else {
        throw new Error(`Failed to load theme preference: ${response.status}`);
      }
    } catch (err) {
      console.warn('Failed to load user theme preference:', err);
      // Fall back to localStorage or light theme
      const fallbackTheme = localStorage.getItem('theme') || 'light';
      setTheme(fallbackTheme);
      applyTheme(fallbackTheme);
      // Don't show error for missing API endpoints, only for actual network issues
      if (err.message.includes('Network') || err.message.includes('fetch')) {
        setError('Using saved theme - will sync when connection is restored');
      } else {
        setError(null); // Don't show errors for missing API endpoints
      }
    } finally {
      setIsLoading(false);
    }
  }, [applyTheme]);

  // Save theme preference to server
  const saveThemePreference = useCallback(async (newTheme) => {
    // Always save locally first
    localStorage.setItem('theme', newTheme);

    if (!isAuthenticated()) {
      // For unauthenticated users, only save locally
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch('https://deepmlbe.maloyan.xyz/api/user/theme', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ theme: newTheme }),
      });

      if (response.ok) {
        setError(null); // Clear any previous errors
      } else if (response.status === 401) {
        // Token expired, local storage already updated
        console.warn('Token expired, theme saved locally');
        setError(null); // Don't show error for expired tokens
      } else if (response.status === 404) {
        // API endpoint doesn't exist yet, theme saved locally
        console.warn('Theme API not implemented yet, saved locally');
        setError(null); // Don't show error for missing API
      } else {
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (err) {
      // Network or other errors - local storage already updated
      console.warn('Failed to sync theme preference to server:', err.message);
      // Only show error for actual network problems, not missing API
      if (err.message.includes('Network') || err.message.includes('fetch')) {
        setError('Theme saved locally - will sync when connection is restored');
      } else {
        setError(null); // Don't show errors for missing API endpoints
      }
    }
  }, []);

  // Toggle between light and dark themes
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    applyTheme(newTheme);

    // Save preference (with debouncing to avoid rapid API calls)
    const timeoutId = setTimeout(() => {
      saveThemePreference(newTheme);
    }, 300);

    // Clean up previous timeout
    return () => clearTimeout(timeoutId);
  }, [theme, applyTheme, saveThemePreference]);

  // Set specific theme
  const setThemeValue = useCallback((newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') {
      console.warn(`Invalid theme: ${newTheme}. Must be 'light' or 'dark'`);
      return;
    }

    setTheme(newTheme);
    applyTheme(newTheme);
    saveThemePreference(newTheme);
  }, [applyTheme, saveThemePreference]);

  // Load theme on mount and when authentication state changes
  useEffect(() => {
    loadUserThemePreference();
  }, [loadUserThemePreference]);

  // Listen for authentication changes (user login/logout)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'access_token' || e.key === 'token') {
        // Auth state changed, reload theme preference
        loadUserThemePreference();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadUserThemePreference]);

  // Listen for focus events to sync theme across tabs
  useEffect(() => {
    const handleFocus = () => {
      if (isAuthenticated()) {
        loadUserThemePreference();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadUserThemePreference]);

  // Initialize theme on first load
  useEffect(() => {
    // Apply the initial theme immediately to prevent flash
    const initialTheme = localStorage.getItem('theme') || 'light';
    applyTheme(initialTheme);
  }, [applyTheme]);

  const value = {
    theme,
    isLoading,
    error,
    toggleTheme,
    setTheme: setThemeValue,
    isAuthenticated: isAuthenticated(),
    refreshTheme: loadUserThemePreference,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Export ThemeContext for direct access if needed
export { ThemeContext };